import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";

export default class extends Controller {
  static values = {
    fontListingExtId: String,
    spaceUrl: String
  }

  connect() {
    // Sort variants on initial load
    this.sortVariants();
    // Add form validation
    this.addFormValidation();
  }

  addFormValidation() {
    const form = this.element.closest('form');
    if (!form) return;

    // Add submit event listener
    form.addEventListener('submit', (event) => {
      if (!this.validateFontFiles()) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });
  }

  validateFontFiles() {
    const variantContainers = document.querySelectorAll('#font-variants-container > div[data-weight]');
    let isValid = true;
    let errorMessages = [];

    // Get selected file types from checkboxes
    const fileTypeCheckboxes = document.querySelectorAll('input[name="font_listing[file_types][]"]:checked');
    const requiredFileTypes = Array.from(fileTypeCheckboxes).map(cb => cb.value);

    // If no file types selected, validate that at least one is selected
    if (requiredFileTypes.length === 0) {
      alert('파일 형식을 최소 하나 이상 선택해주세요.');
      return false;
    }

    variantContainers.forEach(container => {
      const weight = container.dataset.weight;

      // Skip if variant is marked for destruction
      const destroyField = container.querySelector('input[name*="_destroy"][value="1"]');
      if (destroyField) return;

      // Check if required file types exist
      const missingTypes = [];
      requiredFileTypes.forEach(fileType => {
        const field = container.querySelector(`input[data-font-upload--font-file-uploader-target="cdnKeyField"][data-file-type="${fileType}"]`);
        const hasFile = field && field.value && field.value.trim() !== '';

        if (!hasFile) {
          missingTypes.push(fileType.toUpperCase());
        }
      });

      if (missingTypes.length > 0) {
        isValid = false;
        errorMessages.push(`Weight ${weight}: ${missingTypes.join(', ')} 파일이 필수입니다`);

        // Highlight the container
        container.classList.add('border-red-500', 'bg-red-50', 'dark:bg-red-900/20');

        // Remove highlight after 10 seconds
        setTimeout(() => {
          container.classList.remove('border-red-500', 'bg-red-50', 'dark:bg-red-900/20');
        }, 10000);
      }
    });

    if (!isValid) {
      // Show error message
      const message = errorMessages.join('\n');
      alert(`폰트 파일 누락:\n\n${message}\n\n각 Weight별로 선택한 파일 형식을 모두 업로드해주세요.`);
    }

    return isValid;
  }

  toggleWeight(event) {
    const checkbox = event.target;
    const weight = checkbox.dataset.weight;

    if (checkbox.checked) {
      this.addVariant(weight);
    } else {
      if (confirm(`Weight ${weight}의 모든 데이터가 삭제됩니다. 계속하시겠습니까?`)) {
        this.removeVariant(event);
      } else {
        checkbox.checked = true;
      }
    }
  }

  async addVariant(weight) {
    // Check if variant already exists
    const existingVariant = document.querySelector(`#font-variants-container [data-weight="${weight}"]`);

    if (existingVariant) {
      // Check if it's marked for deletion
      const destroyField = existingVariant.querySelector('input[name*="_destroy"]');
      if (destroyField && destroyField.value === "1") {
        // Cancel deletion
        destroyField.value = "0";
        existingVariant.style.display = "";
        console.log(`Weight ${weight} 삭제 취소됨`);
        return;
      }
      // Already active variant, do nothing
      return;
    }

    // Get currently selected file types
    const fileTypeCheckboxes = document.querySelectorAll('input[name="font_listing[file_types][]"]:checked');
    const selectedFileTypes = Array.from(fileTypeCheckboxes).map(cb => cb.value);

    try {
      const url = `/designers/${this.spaceUrlValue}/font_listings/${this.fontListingExtIdValue}/add_font_variant`;
      const headers = {
        'Accept': 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/json'
      };

      const response = await api.post(url, {
        weight: weight,
        file_types: selectedFileTypes
      }, headers);

      if (response.status === 200) {
        const turboStream = response.data;
        Turbo.renderStreamMessage(turboStream);

        // Sort variants after adding
        this.sortVariants();
      }
    } catch (error) {
      console.error("Failed to add variant:", error);
      alert("폰트 굵기 추가에 실패했습니다.");
    }
  }

  sortVariants() {
    const container = document.querySelector('#font-variants-container');
    if (!container) return;

    // Find the actual variants container (might be nested in form fields)
    const variantDivs = container.querySelectorAll('div[data-weight]');
    if (variantDivs.length === 0) return;

    // Get all variant divs with their complete structure
    const variants = Array.from(variantDivs).filter(div => {
      // Make sure it's a complete variant container, not a nested element
      return div.classList.contains('border');
    });

    if (variants.length === 0) return;

    // Define weight order
    const weightOrder = {
      '100': 1, '200': 2, '300': 3, '400': 4, 'normal': 4,
      '500': 5, '600': 6, '700': 7, '800': 8, '900': 9, '950': 10
    };

    // Sort variants by weight
    variants.sort((a, b) => {
      const weightA = weightOrder[a.dataset.weight] || 999;
      const weightB = weightOrder[b.dataset.weight] || 999;
      return weightA - weightB;
    });

    // Find the parent that contains all variants
    const variantsParent = variants[0].parentElement;

    // Reorder DOM elements within their parent
    variants.forEach(variant => {
      variantsParent.appendChild(variant);
    });
  }


  removeVariant(event) {
    const weight = event.currentTarget.dataset.weight || event.target.dataset.weight;

    // Confirm before removing
    if (!confirm(`Weight ${weight}의 모든 데이터가 삭제됩니다. 계속하시겠습니까?`)) {
      return;
    }

    const variantDiv = document.querySelector(`#font-variants-container [data-weight="${weight}"]`);

    if (variantDiv) {
      // Check if it's a persisted record (has ID field)
      const idField = variantDiv.querySelector('input[name*="[id]"]');
      const destroyField = variantDiv.querySelector('input[name*="_destroy"]');

      if (idField && destroyField) {
        // Persisted record: mark for destruction
        destroyField.value = "1";
        variantDiv.style.display = "none";
        console.log(`Weight ${weight} marked for deletion (persisted record)`);
      } else {
        // New record: remove from DOM completely
        variantDiv.remove();
        console.log(`Weight ${weight} removed from DOM (new record)`);
      }

      // Uncheck the weight selector
      const checkbox = document.querySelector(`input[type="checkbox"][data-weight="${weight}"]`);
      if (checkbox) {
        checkbox.checked = false;
      }
    }
  }
}